import { useEffect, useState } from "react";

/**
 * Resolves a stable DOM node (e.g. `#top-nav-subheader`) for `createPortal`.
 *
 * Keeps watching after the node is found: the shell can replace a slot
 * wholesale (the page header zone swaps implementations when a redesigned
 * detail page reports in), and a portal into the detached copy renders into
 * nothing. When the node leaves the document, the next mutation re-resolves.
 */
export function usePortalTarget(id: string): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(() =>
    typeof document !== "undefined" ? document.getElementById(id) : null,
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const syncTarget = () => {
      const element = document.getElementById(id);
      setTarget((current) => (current === element ? current : element));
    };

    syncTarget();
    const frame = requestAnimationFrame(syncTarget);
    const observer = new MutationObserver(syncTarget);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [id]);

  return target;
}
